'use client'

import React from 'react';
import { builder, BuilderContent } from "@builder.io/sdk";
import { useEffect, useState, useRef } from "react";
import { Skeleton } from '@/components/ui/skeleton';
import { SpacingType, sectionVariants } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageContext } from '../builder';
import { SectionWrapper } from '../components/SectionWraper';
import { Card } from '../ui/card';
import Link from 'next/link';
import { cn, variantBackgroundColors } from '@/lib/utils';

interface BusinessGuideListingProps {
  spacing?: SpacingType;
  darkMode?: boolean; 
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  attributes?: React.HTMLProps<HTMLElement>;
}

interface CategoryState {
  offset: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  items: BuilderContent[];
}

export function BusinessGuideListing({ spacing = 'xlarge', darkMode = false, variant = 'A', usedInPageNav = false, pageNavTitle = 'Business Guides', themeChangeOnScroll = false, attributes }: BusinessGuideListingProps) {
  const [loading, setLoading] = useState(true);
  const [categoryStates, setCategoryStates] = useState<{ [key: string]: CategoryState }>({});
  const [activeCategory, setActiveCategory] = useState<string>('');
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const pageFields = React.useContext(PageContext);
  const { typeData, articlesByCategory, itemsPerPage } = (pageFields.data as unknown as { typeData?: { category: string }[]; articlesByCategory?: { [key: string]: { title: string; url: string; type: string }[] }; itemsPerPage?: number }) ?? {};

  const loadMore = React.useCallback(async (categoryKey: string) => {
    setCategoryStates(prev => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        isLoadingMore: true
      }
    }));

    try {
      const currentState = categoryStates[categoryKey];
      const newOffset = currentState.offset + (itemsPerPage ?? 0);
      
      const newArticles = await builder.getAll('business-guides', {
        limit: (itemsPerPage ?? 0) + 1,
        offset: newOffset,
        query: {
          'data.type': categoryKey
        },
        options: {
          noTargeting: true,
        },
        preview: true,
        noTraverse: true,
        noEditorUpdates: true,
        prerender: false,
      });

      setCategoryStates(prev => ({
        ...prev,
        [categoryKey]: {
          offset: newOffset,
          hasMore: newArticles.length > (itemsPerPage ?? 0),
          isLoadingMore: false,
          items: [...prev[categoryKey].items, ...newArticles.slice(0, itemsPerPage ?? 0)]
        }
      }));
    } catch (err) {
      console.error("Load more failed for category:", categoryKey, err);
      setCategoryStates(prev => ({
        ...prev,
        [categoryKey]: {
          ...prev[categoryKey],
          isLoadingMore: false
        }
      }));
    }
  }, [categoryStates, itemsPerPage]);

  useEffect(() => {
    if (articlesByCategory && typeData) {
      const initialStates = typeData.reduce((acc: { [key: string]: CategoryState }, category: { category: string }) => {
        const categoryKey = category.category;
        acc[categoryKey] = {
          offset: 0,
          hasMore: (articlesByCategory[categoryKey]?.length || 0) > (itemsPerPage ?? 0),
          isLoadingMore: false,
          items: (articlesByCategory[categoryKey]?.slice(0, itemsPerPage ?? 0) || []) as unknown as BuilderContent[]
        };
        return acc;
      }, {});
      
      setCategoryStates(initialStates);
      setLoading(false);
    }     
  }, [articlesByCategory, typeData, itemsPerPage]);

  useEffect(() => {
    if (!typeData) return;

    // Wait for the next tick to ensure refs are assigned
    const timeoutId = setTimeout(() => {
      const observerOptions = {
        root: null,
        rootMargin: '-10% 0px -60% 0px',
        threshold: 0
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const categoryId = entry.target.id;
            setActiveCategory(categoryId);
          }
        });
      }, observerOptions);

      // Observe all category sections
      typeData.forEach((category: { category: string }) => {
        const categoryKey = category.category;
        const element = sectionRefs.current[categoryKey];
        if (element) {
          observer.observe(element);
        }
      });

      return () => {
        typeData.forEach((category: { category: string }) => {
          const categoryKey = category.category;
          const element = sectionRefs.current[categoryKey];
          if (element) {
            observer.unobserve(element);
          }
        });
        observer.disconnect();
      };
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [typeData, categoryStates]);

  const inputBackgroundColor = variantBackgroundColors(darkMode, variant);

  if (loading) {
    return (
      <SectionWrapper
        usedInPageNav={usedInPageNav}
        pageNavTitle={pageNavTitle}
        themeChangeOnScroll={themeChangeOnScroll}
        spacing={spacing}
        variant={variant}
        darkMode={darkMode}
        heroSection={false}
        componentName="business-guides-list"
        attributes={attributes}
      >
        <div className="container">
          <div className="relative grid gap-16 mt-4 md:mt-0 md:gap-10 lg:gap-16 2xl:gap-20 md:grid-cols-5 2xl:grid-cols-6">
            <aside className="top-24 flex h-fit w-full flex-col gap-5 md:sticky md:col-span-2 lg:col-span-2 lg:max-w-80 2xl:min-w-[240px]">
              <div className="flex flex-col gap-6 p-6 bg-muted rounded-xl">
                <Skeleton className={cn("bg-white h-6 w-24")} />
                <div className="flex flex-col">
                  {[...Array(4)].map((_, index) => (
                    <div key={index} className="block border-l border-border py-2 pl-4">
                      <Skeleton className={cn("bg-white h-5 w-32")} />
                    </div>
                  ))}
                </div>
              </div>
            </aside>
            <div className="flex flex-col gap-10 lg:gap-20 md:col-span-3 2xl:col-span-4">
              {[...Array(3)].map((_, categoryIndex) => (
                <div key={categoryIndex} className="flex flex-col gap-6 lg:gap-10">
                  <Skeleton className={cn(inputBackgroundColor, "h-8 w-48")} />
                  <div className="grid grid-cols-1 gap-x-5 gap-y-6 lg:grid-cols-2">
                    {[...Array(4)].map((_, cardIndex) => (
                      <Card key={cardIndex} className="min-h-[210px] flex flex-col justify-between p-6">
                        <Skeleton className={cn(inputBackgroundColor, "h-6 w-full mb-2")} />
                        <Skeleton className={cn(inputBackgroundColor, "h-6 w-3/4 mb-4")} />
                        <Skeleton className={cn(inputBackgroundColor, "h-6 w-20 rounded-full")} />
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="business-guides-list"
      attributes={attributes}
    >
      <div className="container">
        {typeData?.length && typeData.length > 0 && (
          <>
            <div className="relative grid gap-16 mt-4 md:mt-0 md:gap-10 lg:gap-16 2xl:gap-20 md:grid-cols-5 2xl:grid-cols-6">
              <aside className="top-24 flex h-fit w-full flex-col gap-5 md:sticky md:col-span-2 lg:col-span-2 lg:max-w-80 2xl:min-w-[240px]">
                <div className="flex flex-col gap-6 p-6 bg-muted rounded-xl">
                  <div className="paragraph-base font-bold">
                    Categories
                  </div>
                  <div className="flex flex-col">
                    {typeData?.map((category: { category: string }) => {
                      const categoryKey = category.category;
                      const hasItems = categoryStates[categoryKey]?.items && categoryStates[categoryKey].items.length > 0;
                      const isActive = activeCategory === categoryKey;
                      
                      // Only show category if it has items
                      if (!hasItems) return null;
                      
                      return (
                        <div 
                          key={categoryKey} 
                          className={cn("paragraph-base  block border-l border-border py-2 pl-4 transition-all duration-300 cursor-pointer",
                            isActive && 'border-brand text-brand' 
                          )}
                          onClick={() => {
                            const element = sectionRefs.current[categoryKey];
                            if (element) {
                              const yOffset = -110;
                              const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                              window.scrollTo({ top: y, behavior: 'smooth' });
                            }
                          }}
                        >
                          {categoryKey}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </aside>
              <div className="flex flex-col gap-10 lg:gap-20 md:col-span-3 2xl:col-span-4"> 
                {typeData?.map((category: { category: string }) => {
                  const categoryKey = category.category;
                  return (
                    categoryStates[categoryKey]?.items && categoryStates[categoryKey].items.length > 0 && (
                      <div 
                        key={categoryKey} 
                        data-aos="fade-up" 
                        id={categoryKey}
                        ref={(el) => {
                          sectionRefs.current[categoryKey] = el;
                        }}
                      >
                        <h3 className="text-gradient-primary mb-6 lg:mb-10" >{categoryKey}</h3>
                        <div className="grid grid-cols-1 gap-x-5 gap-y-6 lg:grid-cols-2">
                          {categoryStates[categoryKey].items.map((item: BuilderContent) => (
                            <Link href={item.data?.url ?? ''}  key={item.id}>
                              <Card className="min-h-[210px] flex flex-col justify-between">
                                <h6>{item.data?.title}</h6>
                                <Badge variant="secondary" bold>{item.data?.type}</Badge>
                              </Card>
                            </Link>
                          ))}
                        </div>
                        {categoryStates[categoryKey]?.hasMore && (
                          <div className="mt-4 lg:mt-10 text-center">
                            <Button 
                              variant="outline" 
                              size="lg" 
                              className="w-full md:w-auto cursor-pointer"
                              onClick={() => loadMore(categoryKey)}
                              disabled={!categoryStates[categoryKey]?.hasMore || categoryStates[categoryKey]?.isLoadingMore}
                            > 
                              {categoryStates[categoryKey]?.isLoadingMore ? 'Loading...' : 'View more'}
                            </Button>
                          </div>
                        )}
                      </div>
                  ));
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </SectionWrapper>
  );
} 